import React, { useRef, useState } from "react";
import "./css/NfcReviews.css";
import { Link } from "react-router-dom";
import ConfirmationModal from "../components/ConfirmationModal";

const steps = [
    {
        id: 1,
        title: "Escolha o modelo",
        description: "Cartão, placa de acrílico ou adesivo NFC — você escolhe o formato que combina com seu balcão ou vitrine.",
    },
    {
        id: 2,
        title: "Configuramos o link",
        description: "Programamos o chip para levar seu cliente direto para a tela de avaliação do Google, sem passos extras.",
    },
    {
        id: 3,
        title: "Você recebe pronto",
        description: "Basta aproximar o celular no cartão. Nenhum aplicativo ou cadastro é necessário para o cliente.",
    },
    {
        id: 4,
        title: "Mais avaliações, mais confiança",
        description: "Menos fricção significa mais clientes deixando avaliações reais — fortalecendo sua reputação online.",
    },
];

const plans = [
    {
        id: 1,
        name: "Cartão NFC",
        price: "Entre em contato!",
        detail: "Cartão em PVC no tamanho de um cartão de crédito, pronto para usar no caixa ou balcão.",
    },
    {
        id: 2,
        name: "Placa de Acrílico",
        price: "Entre em contato!",
        detail: "Placa de acrílico premium para vitrine, mesa ou recepção — mais visibilidade e durabilidade.",
    },
    {
        id: 3,
        name: "Adesivo NFC",
        price: "Entre em contato!",
        detail: "Opção compacta para colar em qualquer superfície, ideal para múltiplos pontos no estabelecimento.",
    },
];

const faqs = [
    {
        id: 1,
        q: "Preciso instalar algum aplicativo?",
        a: "Não. O cliente apenas aproxima o celular do cartão e o navegador abre direto na página de avaliação do Google.",
    },
    {
        id: 2,
        q: "Funciona em qualquer celular?",
        a: "Funciona na grande maioria dos smartphones modernos (Android e iPhone) com NFC ativado, recurso presente por padrão há vários anos.",
    },
    {
        id: 3,
        q: "Posso trocar o link depois?",
        a: "Sim, o link pode ser reprogramado sempre que necessário, sem custo de reposição do cartão.",
    },
    {
        id: 4,
        q: "Qual o prazo de entrega?",
        a: "Normalmente entre 5 e 10 dias úteis após a confirmação do pedido, dependendo do modelo escolhido.",
    },
];

const NfcReviews = () => {
    const formRef = useRef();
    const [status, setStatus] = useState(null);

    const sendEmail = async (e) => {
        e.preventDefault();

        const formData = new FormData(formRef.current);
        const data = Object.fromEntries(formData.entries());

        try {
            const response = await fetch("https://website-backend-production-2163.up.railway.app/api/email/send-email", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(data)
            });

            if (response.ok) {
                setStatus("success");
                formRef.current.reset();
            } else {
                setStatus("error");
            }
        } catch (error) {
            console.error("Error sending message:", error);
            setStatus("error");
        }
    };

    return (
        <section className="nfc-section">
            <Link to="/" className="nfc-home-button">← Voltar</Link>

            <div className="nfc-hero">
                <span className="nfc-badge">Novo Serviço</span>
                <h1 className="nfc-title">Cartões e Placas NFC para Google Reviews</h1>
                <p className="nfc-subtitle">
                    Transforme a experiência de avaliação do seu negócio. Seu cliente aproxima o celular
                    e é direcionado diretamente para deixar uma avaliação no Google — sem digitar nada,
                    sem procurar o link, sem esforço.
                </p>
                <a href="#nfc-contact" className="nfc-cta-button">Solicitar Orçamento</a>
            </div>

            <div className="nfc-container">
                <h2 className="nfc-section-title">Como Funciona</h2>
                <div className="nfc-steps-grid">
                    {steps.map((step) => (
                        <div className="nfc-step-card" key={step.id}>
                            <div className="nfc-step-number">{step.id}</div>
                            <h3 className="nfc-step-title">{step.title}</h3>
                            <p className="nfc-step-description">{step.description}</p>
                        </div>
                    ))}
                </div>
            </div>

            <div className="nfc-container">
                <h2 className="nfc-section-title">Modelos e Valores</h2>
                <p className="nfc-section-note">
                    Os valores podem variar conforme quantidade e personalização. Entre em contato para um orçamento exato.
                </p>
                <div className="nfc-plans-grid">
                    {plans.map((plan) => (
                        <div className="nfc-plan-card" key={plan.id}>
                            <h3 className="nfc-plan-name">{plan.name}</h3>
                            <p className="nfc-plan-price">{plan.price}</p>
                            <p className="nfc-plan-detail">{plan.detail}</p>
                        </div>
                    ))}
                </div>
            </div>

            <div className="nfc-container">
                <h2 className="nfc-section-title">Por que confiar nesse serviço</h2>
                <div className="nfc-trust-grid">
                    <div className="nfc-trust-item">
                        <span className="nfc-trust-icon">⚡</span>
                        <p>Configuração rápida e sem complicação técnica para o comerciante.</p>
                    </div>
                    <div className="nfc-trust-item">
                        <span className="nfc-trust-icon">🔒</span>
                        <p>Chips NFC de qualidade, testados antes do envio.</p>
                    </div>
                    <div className="nfc-trust-item">
                        <span className="nfc-trust-icon">📈</span>
                        <p>Aumento real no volume de avaliações, sem esquemas artificiais.</p>
                    </div>
                    <div className="nfc-trust-item">
                        <span className="nfc-trust-icon">🤝</span>
                        <p>Suporte direto comigo durante toda a configuração e após a entrega.</p>
                    </div>
                </div>
            </div>

            <div className="nfc-container">
                <h2 className="nfc-section-title">Perguntas Frequentes</h2>
                <div className="nfc-faq-list">
                    {faqs.map((faq) => (
                        <div className="nfc-faq-item" key={faq.id}>
                            <h3 className="nfc-faq-question">{faq.q}</h3>
                            <p className="nfc-faq-answer">{faq.a}</p>
                        </div>
                    ))}
                </div>
            </div>

            <div className="nfc-container" id="nfc-contact">
                <h2 className="nfc-section-title">Fale Comigo</h2>
                <p className="nfc-section-note">
                    Preencha o formulário abaixo com os detalhes do seu negócio e retornarei o contato com um orçamento.
                </p>
                <div className="nfc-contact-box">
                    <form ref={formRef} className="nfc-contact-form" onSubmit={sendEmail}>
                        <input type="text" name="name" placeholder="Seu nome" required />
                        <input type="email" name="user_email" placeholder="Seu e-mail" required />
                        <input type="text" name="business" placeholder="Nome do estabelecimento" />

                        <select name="reason" required defaultValue="">
                            <option value="" disabled>Quantidade desejada</option>
                            <option value="1 unidade">1 unidade</option>
                            <option value="2 a 5 unidades">2 a 5 unidades</option>
                            <option value="6 ou mais unidades">6 ou mais unidades</option>
                            <option value="Ainda não sei">Ainda não sei</option>
                        </select>

                        <textarea name="message" placeholder="Conte um pouco sobre seu negócio e o que precisa..." rows="5" required></textarea>

                        <button type="submit">Enviar Mensagem</button>
                    </form>
                </div>

                <div className="nfc-direct-contact">
                    <a href="mailto:flppprojects@outlook.com" className="nfc-direct-link">
                        <i className="bi bi-envelope-fill"></i> flppprojects@outlook.com
                    </a>
                </div>

                <div className="nfc-support-banner">
                    <span>Já é cliente e está com algum problema no cartão?</span>
                    <Link to="/nfc-support" className="nfc-support-link">Acessar Central de Suporte →</Link>
                </div>
            </div>

            <ConfirmationModal
                status={status}
                onClose={() => setStatus(null)}
                onContinue={() => setStatus(null)}
            />
        </section>
    );
};

export default NfcReviews;
